import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OfficialPrescription({ patient, prescription, via, isAvulsa = false, showQRCode = true }) {
  const [healthUnit, setHealthUnit] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [validationCode, setValidationCode] = useState(null);
  const [qrCodeReady, setQrCodeReady] = useState(false);
  
  const currentDate = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  
  // Texto da via (só aparece na impressão)
  const viaText = via ? `${via}ª VIA` : null;

  // Gerar URL de validação - usar URL do frontend para a página de validação
  const validationUrl = prescription?.id ? `${BACKEND_URL}/validar/${prescription.id}` : null;
  
  // Buscar o código de validação do backend para garantir consistência
  useEffect(() => {
    const fetchValidationCode = async () => {
      // Para receitas avulsas com QR Code habilitado, gerar código local
      if (isAvulsa && showQRCode) {
        // Gerar código de validação local para receitas avulsas
        const timestamp = Date.now();
        const code = `AVL-${timestamp.toString(36).toUpperCase().slice(-8)}`;
        setValidationCode(code);
        setQrCodeReady(true);
        return;
      }
      
      // Para receitas normais, buscar do backend
      if (prescription?.id && !isAvulsa && showQRCode) {
        try {
          const response = await axios.get(`${API}/validate/${prescription.id}`);
          if (response.data?.validation_code) {
            setValidationCode(response.data.validation_code);
            setQrCodeReady(true);
          }
        } catch (error) {
          console.error('Erro ao buscar código de validação:', error);
          // Fallback: gerar código localmente se API falhar
          setValidationCode(prescription.id.substring(0, 12).toUpperCase());
          setQrCodeReady(true);
        }
      }
    };
    fetchValidationCode();
  }, [prescription?.id, isAvulsa, showQRCode]);

  useEffect(() => {
    // Get current user from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    
    const fetchHealthUnit = async () => {
      if (patient.unit_id) {
        try {
          const response = await axios.get(`${API}/health-units/${patient.unit_id}`);
          setHealthUnit(response.data);
        } catch (error) {
          console.error('Erro ao buscar unidade:', error);
        }
      }
    };
    fetchHealthUnit();
  }, [patient.unit_id]);

  // Group medications by via for display
  const medicationsByVia = useMemo(() => {
    // If we have medication_groups, use them
    if (prescription.medication_groups && prescription.medication_groups.length > 0) {
      return prescription.medication_groups;
    }
    
    // Otherwise, group by the via field on each medication or use prescription.uso
    const groups = {};
    for (const med of prescription.medicamentos || []) {
      const via = med.via || prescription.uso || 'USO ORAL';
      if (!groups[via]) {
        groups[via] = [];
      }
      groups[via].push(med);
    }
    
    return Object.entries(groups).map(([via, meds]) => ({
      via,
      medicamentos: meds
    }));
  }, [prescription]);

  // Calculate font size based on total number of medications
  const totalMedCount = prescription.medicamentos?.length || 0;
  
  const fontSizeConfig = useMemo(() => {
    const medCount = totalMedCount;
    
    // For 1-3 medications: largest fonts
    if (medCount <= 3) {
      return {
        medicationSize: 'text-xl',
        instructionSize: 'text-lg',
        patientNameSize: 'text-3xl',
        usoSize: 'text-xl',
        spacing: 'mb-5',
        containerSpacing: 'space-y-5'
      };
    } 
    // For 4-5 medications: large fonts
    else if (medCount <= 5) {
      return {
        medicationSize: 'text-lg',
        instructionSize: 'text-base',
        patientNameSize: 'text-2xl',
        usoSize: 'text-lg',
        spacing: 'mb-4',
        containerSpacing: 'space-y-4'
      };
    } 
    // For 6-8 medications: medium-large fonts (improved from before)
    else if (medCount <= 8) {
      return {
        medicationSize: 'text-base',
        instructionSize: 'text-sm',
        patientNameSize: 'text-xl',
        usoSize: 'text-base',
        spacing: 'mb-3',
        containerSpacing: 'space-y-3'
      };
    } 
    // For 9-12 medications: medium fonts
    else if (medCount <= 12) {
      return {
        medicationSize: 'text-sm',
        instructionSize: 'text-xs',
        patientNameSize: 'text-lg',
        usoSize: 'text-sm',
        spacing: 'mb-2',
        containerSpacing: 'space-y-2'
      };
    } 
    // For 13+ medications: smaller fonts (but still readable)
    else {
      return {
        medicationSize: 'text-xs',
        instructionSize: 'text-xs',
        patientNameSize: 'text-base',
        usoSize: 'text-xs',
        spacing: 'mb-1.5',
        containerSpacing: 'space-y-1.5'
      };
    }
  }, [totalMedCount]);

  return (
    <div 
      className="prescription-print-page bg-white text-black font-arial"
      style={{
        width: '100%',
        maxWidth: '210mm',
        padding: '6mm 10mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        margin: '0 auto'
      }}
    >
      {/* Via indicator - only shows in print */}
      {viaText && (
        <div className="absolute top-1 right-2 text-xs font-bold text-gray-600 print:text-black">
          {viaText}
        </div>
      )}
      
      {/* Header - Compacto */}
      <div className="text-center border-b-2 border-black pb-1 mb-2 flex-shrink-0">
        <div className="flex items-start justify-between">
          <img 
            src="/assets/brasao-sao-domingos.png" 
            alt="Brasão São Domingos do Prata"
            className="w-14 h-14 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Brasao-saodomingosdoprata-mg.jpg/120px-Brasao-saodomingosdoprata-mg.jpg";
            }}
          />
          <div className="flex-1 mx-2">
            <h1 className="text-xs font-bold mb-0.5">Fundo Municipal de Saúde de São Domingos do Prata</h1>
            <h2 className="text-[10px] font-bold mb-0.5">SECRETARIA MUNICIPAL DE SAÚDE E AÇÃO SOCIAL</h2>
            <p className="text-[9px] mb-0">Rua Prof. Cristiano Morais, nº 38-Centro São Domingos do Prata-MG</p>
            <p className="text-[9px] mb-0">Telefone: (31) 3868-1310</p>
            <p className="text-[9px] mb-0">E-mail: dmsac@saodomingosdoprata.mg.gov.br</p>
            <p className="text-[9px]">CNPJ: 11.466.385/0001-68-Inscrição Estadual: Isento</p>
          </div>
          <img 
            src="/assets/logo-sus.png" 
            alt="Logo SUS"
            className="w-14 h-14 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/Logo_SUS.svg/120px-Logo_SUS.svg.png";
            }}
          />
        </div>
      </div>

      {/* Patient Name with "NOME:" label */}
      <div className="mb-4 flex-shrink-0">
        <h2 className={`${fontSizeConfig.patientNameSize} font-bold text-center uppercase`}>
          <span className="font-semibold">NOME: </span>{patient.nome}
        </h2>
        {/* Nome personalizado da receita (se existir) */}
        {prescription.name && (
          <p className="text-center text-sm text-gray-600 mt-1 italic">
            {prescription.name}
          </p>
        )}
      </div>

      {/* Dates - Renovação e Próxima Renovação - NÃO aparece na Receita Avulsa */}
      {!isAvulsa && (
        <div className="mb-3 flex-shrink-0 flex justify-between text-xs text-gray-700 border-b border-gray-200 pb-2">
          <div className="flex items-center gap-1">
            <span className="font-semibold">Renovação:</span>
            <span>
              {/* Usa data_impressao se existir, senão usa data de hoje (impressão atual) */}
              {prescription.data_impressao 
                ? new Date(prescription.data_impressao).toLocaleDateString('pt-BR')
                : new Date().toLocaleDateString('pt-BR')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-semibold">Próxima Renovação:</span>
            <span>
              {prescription.data_validade 
                ? new Date(prescription.data_validade).toLocaleDateString('pt-BR')
                : 'N/A'}
            </span>
          </div>
        </div>
      )}

      {/* Medications grouped by Via de Administração */}
      <div className={`${fontSizeConfig.containerSpacing} flex-grow mt-2`}>
        {medicationsByVia.map((group, groupIndex) => {
          return (
            <div key={groupIndex} className={groupIndex > 0 ? 'mt-6 pt-4' : ''}>
              {/* Via Header - sublinhado como no exemplo */}
              <div className="mb-4 text-center">
                <h3 className={`${fontSizeConfig.usoSize} font-bold uppercase underline`}>{group.via}</h3>
              </div>
              
              {/* Medications in this group - numeração reinicia em cada seção */}
              <div className={fontSizeConfig.containerSpacing}>
                {group.medicamentos.map((med, medIndex) => {
                  // Format medication line
                  const medicationName = med.name;
                  const dosage = med.dosage ? `${med.dosage} ${(med.dosage_unit || 'MG').toLowerCase()}` : '';
                  
                  // Determine the packaging info
                  let packagingInfo = '';
                  if (med.packaging_type === 'uso_continuo') {
                    packagingInfo = 'USO CONTÍNUO';
                  } else if (med.quantity) {
                    const qty = parseInt(med.quantity) || 1;
                    let packagingLabel = qty === 1 ? 'CP' : 'CPS';
                    
                    if (med.packaging_type === 'frascos') {
                      packagingLabel = qty === 1 ? 'FRASCO' : 'FRASCOS';
                    } else if (med.packaging_type === 'cartelas') {
                      packagingLabel = qty === 1 ? 'CARTELA' : 'CARTELAS';
                    } else if (med.packaging_type === 'ampolas') {
                      packagingLabel = qty === 1 ? 'AMPOLA' : 'AMPOLAS';
                    } else if (med.packaging_type === 'tubos') {
                      packagingLabel = qty === 1 ? 'TUBO' : 'TUBOS';
                    } else if (med.packaging_type === 'caixas') {
                      packagingLabel = qty === 1 ? 'CX' : 'CXS';
                    } else if (med.packaging_type === 'envelopes') {
                      packagingLabel = qty === 1 ? 'ENVELOPE' : 'ENVELOPES';
                    } else if (med.packaging_type === 'unidades') {
                      packagingLabel = qty === 1 ? 'UNIDADE' : 'UNIDADES';
                    } else if (med.packaging_type === 'comprimidos') {
                      packagingLabel = qty === 1 ? 'COMPRIMIDO' : 'COMPRIMIDOS';
                    }
                    packagingInfo = `${med.quantity} ${packagingLabel}`;
                  }
                  
                  return (
                    <div key={medIndex} className={fontSizeConfig.spacing}>
                      {/* Layout em grid para garantir alinhamento horizontal */}
                      <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-baseline">
                        {/* Número + Medicamento */}
                        <span className={`${fontSizeConfig.medicationSize} font-bold whitespace-nowrap`}>
                          {medIndex + 1})
                        </span>
                        {/* Nome do medicamento + Dosagem */}
                        <span className={`${fontSizeConfig.medicationSize} font-bold break-words`}>
                          {medicationName.toUpperCase()}
                          {dosage && ` ${dosage}`}
                        </span>
                        {/* Quantidade */}
                        {packagingInfo && (
                          <span className={`${fontSizeConfig.medicationSize} font-bold whitespace-nowrap text-right`}>
                            {'—'.repeat(5)} {packagingInfo}
                          </span>
                        )}
                      </div>
                      {med.instructions && (
                        <p className={`${fontSizeConfig.instructionSize} mt-1 ml-6 font-bold uppercase`}>{med.instructions}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with QR Code */}
      <div 
        className="prescription-footer pt-3 mt-4 border-t border-black flex-shrink-0"
      >
        <div className="flex justify-between items-end">
          <div className="text-left">
            <p className="text-sm font-bold">São Domingos do Prata</p>
            <p className="text-sm font-bold">{currentDate}</p>
          </div>
          
          {/* QR Code de Validação - aparece se habilitado e tiver código pronto */}
          {showQRCode && qrCodeReady && validationCode && (
            <div className="flex items-center gap-2 qr-code-container">
              <div className="text-right text-xs">
                <p className="text-gray-600">{isAvulsa ? 'Receita Avulsa:' : 'Validar receita:'}</p>
                <p className="font-mono font-bold text-[10px]">{validationCode}</p>
              </div>
              <div className="qr-code-wrapper" style={{ width: '50px', height: '50px' }}>
                <QRCodeSVG 
                  value={isAvulsa ? `AVULSA:${validationCode}` : validationUrl}
                  size={50}
                  level="M"
                  includeMargin={false}
                  style={{ width: '50px', height: '50px', display: 'block' }}
                />
              </div>
            </div>
          )}
          
          {/* Mostra "Sem QR Code" se desabilitado */}
          {!showQRCode && (
            <div className="text-right">
              <p className="text-xs text-gray-400">{isAvulsa ? 'Receita Avulsa' : 'Sem validação digital'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
